import SettingsTabBase from "./SettingsTabBase.jsx";
import "../../widget/fields/SwivuColorField.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { assignViewsState } from "../../../other/utils/otherUtils.jsx";
import { GRANTS } from "../../../other/constants/PrivilegeTypes.jsx";
import { cellMenuItemRemoveRow } from "../../../other/utils/gridCellMenuUtils.jsx";
import { STORE_KEYS } from "../../../other/constants/StoreKeys.jsx";

export default class SwivuSettingsIncentiveCategoriesContainer extends SettingsTabBase {
  static get $name() {
    return "SwivuSettingsIncentiveCategoriesContainer";
  }

  static get type() {
    return "swivusettingsincentivecategoriescontainer";
  }

  get swivuStore() {
    return this.project.getCrudStore(STORE_KEYS.incentiveTagCategoryStore);
  }

  static get configurable() {
    return {
      title: "Points Categories",
      items: [
        {
          type: WIDGET_KEYS.swivuPreferenceStyleGrid,
          ref: "categoryGrid",
          cls: "!p-0 w-full",
          height: "100%",
          emptyText: "No categories created yet",
          features: {
            sort: false,
            group: false,
            cellMenu: {
              items: {
                cut: false,
                copy: false,
                paste: false,
                removeRow: {
                  ...cellMenuItemRemoveRow(),
                  onItem: "up.onRemoveCategory"
                }
              }
            }
          },
          columns: [
            {
              id: "name",
              field: "name",
              text: "Category",
              editor: {
                placeholder: "Enter Category",
                required: true
              },
              finalizeCellEdit: "up.onFinalizeCellEdit",
              flex: 1
            },
            {
              id: "description",
              field: "description",
              text: "Description",
              editor: {
                placeholder: "Enter Description"
              },
              finalizeCellEdit: "up.onFinalizeCellEdit",
              flex: 1
            },
            {
              id: "color",
              field: "color",
              text: "Color",
              editor: {
                type: WIDGET_KEYS.colorField,
                placeholder: "Select Color",
                canRemoveColor: true
              },
              finalizeCellEdit: "up.onFinalizeCellEdit",
              renderer({ cellElement, value }) {
                cellElement.style.backgroundColor = value || "transparent";
              },
              flex: 1
            },
            {
              id: "points",
              field: "points",
              text: "Points",
              editor: {
                type: "number",
                placeholder: "Enter Points"
              },
              finalizeCellEdit: "up.onFinalizeCellEdit",
              flex: 1
            }
          ],
          tbar: {
            cls: "swivu-modern-grid-tbar [&_.b-toolbar-content]:items-start [&_.b-toolbar-content]:p-0 [&_.b-toolbar-content]:!min-h-[auto] mb-swivu-base bg-transparent [&_.b-toolbar-content]:!flex-col",
            items: {
              titleContainer: {
                type: "container",
                cls: "flex-1 flex flex-col gap-1",
                items: {
                  title: {
                    type: "displayField",
                    value: "Categories",
                    cls: "swivu-grid-title text-swivu-md font-semibold text-[#282828] dark:text-[#f0f0f0] [&_span]:!p-0"
                  },
                  subtitle: {
                    type: "displayField",
                    value: "Create categories with point values and colors to organize tags for incentives.",
                    cls: "text-swivu-sm text-muted-foreground [&_span]:!p-0"
                  },
                },
              },
            },
          },
          bbar: {
            cls: "justify-end !px-swivu-base",
            items: {
              addButton: {
                type: "button",
                icon: "b-fa b-fa-plus",
                color: "b-green",
                onClick: "up.onAddCategory"
              },
              deleteButton: {
                type: "button",
                icon: "b-fa b-fa-trash",
                color: "b-red",
                onClick: "up.onRemoveCategory"
              }
            }
          }
        }
      ]
    };
  }

  onAddCategory() {
    const { categoryGrid } = this.widgetMap;
    const newRecord = this.swivuStore.createRecord({
      name: "",
      color: "#000000",
      points: 0
    });
    categoryGrid.store.add(newRecord);
    categoryGrid.startEditing({ record: newRecord, field: "name" });
  }

  onRemoveCategory() {
    const { categoryGrid } = this.widgetMap;
    const removeSelected = categoryGrid.selectedRecords;
    if (removeSelected.length) {
      const selectedIds = removeSelected.map((r) => r.id);
      categoryGrid.store.remove(selectedIds);
      this.swivuStore.remove(selectedIds);
    }
  }

  attachState(gridWidget, widgetRef) {
    gridWidget.stateId = `b-settings-tab-${widgetRef}`;
    assignViewsState([gridWidget], this.stateProvider);
  }

  loadRecord(record, context) {
    super.loadRecord(record, context);
    const { categoryGrid } = this.widgetMap;
    categoryGrid.store.data = this.swivuStore.query(() => true, true);
    this.attachState(
      categoryGrid,
      PERSISTENCE_STATE_KEYS.boardSettingIncentiveTagCategoryGrid
    );
  }

  swivuCheckGrants(user) {
    if (!user.can(GRANTS.grant_one)) {
      const { categoryGrid: grid, addButton, deleteButton } = this.widgetMap;
      grid.disabled = true;
      grid.readOnly = true;
      // Disable action buttons in bbar
      addButton.disabled = true;
      deleteButton.disabled = true;
    }
  }

  validateCategoryField(field, value, record) {
    switch (field) {
      case "name": {
        const name = value.trim();
        if (!name) return "Name is required";
        const existing = this.swivuStore.find(
          (categoryRecord) =>
            categoryRecord.name.trim().toLowerCase() === name.toLowerCase() &&
            categoryRecord.id !== record.id
        );
        if (existing) return "Category with same name already exists";
        return true;
      }
      case "color": {
        if (!value) return "Color is required";
        return true;
      }
      case "points": {
        if (value === null) return "Points are required";
        else if (value < 0) return "Points cannot be less than 0";
        return true;
      }
      default:
        return true;
    }
  }

  isCategoryRecordComplete(record) {
    const hasName = record.name.trim().length > 0;
    const hasColor = record.color.trim().length > 0;
    const hasValidPoints = record.point !== null && record.points >= 0;
    return hasName && hasColor && hasValidPoints;
  }

  onFinalizeCellEdit({ value, record, editorContext }) {
    const { column } = editorContext;
    const validation = this.validateCategoryField(column.field, value, record);
    if (validation !== true) return validation;
    record[column.field] = value;
    if (record.isPhantom && this.isCategoryRecordComplete(record)) {
      this.swivuStore.add(record);
    }
    return true;
  }
}

SwivuSettingsIncentiveCategoriesContainer.initClass();
